import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useRef, useCallback } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ref as dbRef, onValue, off } from 'firebase/database';
import { storage, database } from '../../firebase';
import JSZip from 'jszip';
import Papa from 'papaparse';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import UpgradeModal from '../UpgradeModal';
import { bulkUploadCVsOptimized, loadAllJobOptions, createCustomList, createImportedProfile } from './DatabaseOptimizer';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { triggerCommunityAutoSync } from '../../services/communityService';
import './CVUploadManager.css';

const normalizeDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CVUploadManager = ({ isOpen, onClose, jobs = [], onUploadComplete, onDataRefresh, allowNoJob = false, employerUID }) => {
  useUiLanguage();
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const { currentUser } = useAuth();
  const { canBulkUpload } = useFeatureAccess();
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'bulk'
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Use the passed employerUID (company ID) or fallback to current user's UID
  // This ensures collaborators save data under the company's account
  const targetUid = employerUID || currentUser?.uid;

  // Single CV upload state
  const [singleCV, setSingleCV] = useState(null);
  const [singleEmail, setSingleEmail] = useState('');
  const [singleName, setSingleName] = useState('');

  // Bulk upload state - simplified to just zip file
  const [bulkZipFile, setBulkZipFile] = useState(null);

  // Custom list creation state
  const [showCreateCustomList, setShowCreateCustomList] = useState(false);
  const [customListTitle, setCustomListTitle] = useState('');
  const [customListDescription, setCustomListDescription] = useState('');
  const [customListJobDescription, setCustomListJobDescription] = useState('');
  const [customListTargetHireDate, setCustomListTargetHireDate] = useState('');

  // Job options state (posted jobs + custom lists)
  const [jobOptions, setJobOptions] = useState([]);

  const formatJobSubtitle = (option) => {
    if (option.type === 'posted_job') {
      return t('workspace.postedJob');
    }
    if (option.type === 'custom_list') {
      const count = option.data?.applicantCount ?? option.applicantCount ?? 0;
      return t('workspace.customListWithCount', { count });
    }
    return option.subtitle || '';
  };

  // Results state
  const [uploadResults, setUploadResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Real-time batch status state
  const [batchStatus, setBatchStatus] = useState(null);
  const [realTimeProgress, setRealTimeProgress] = useState(null);

  const fileInputRef = useRef(null);
  const bulkZipInputRef = useRef(null);
  const [noJobMode, setNoJobMode] = useState(false);
  const [bulkLocalFiles, setBulkLocalFiles] = useState([]); // PDFs for no-job bulk

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

  // Listen for real-time batch status updates
  const listenForBatchStatus = (batchId, jobData) => {
    // Listen to the batch status under the target company UID
    const batchRef = dbRef(database, `bulk_uploads/${targetUid}/${batchId}`);

    console.log(`🔥 Setting up Firebase listener for batch: ${batchId}`);
    console.log(`🔥 Listener path: bulk_uploads/${targetUid}/${batchId}`);

    const unsubscribe = onValue(batchRef, (snapshot) => {
      const data = snapshot.val();
      console.log(`🔥 Firebase listener triggered - Raw data:`, data);

      if (data) {
        console.log(`🔥 Firebase listener - Status: ${data.status}, Progress: ${data.progress || 'N/A'}`);
        setBatchStatus(data);

        // Update progress based on status
        let progressValue = 30; // Start with ZIP uploaded
        let progressMessage = data.message || '';

        switch (data.status) {
          case 'extracting':
            progressValue = 50; // Step 2 of 4
            progressMessage = 'Extracting CVs from ZIP file...';
            break;
          case 'validating':
            progressValue = 70; // Step 3 of 4
            progressMessage = 'Validating CV files and mapping data...';
            break;
          case 'processing':
            progressValue = 90; // Step 4 of 4
            progressMessage = 'Applying AI filtering and organizing candidates...';
            break;
          case 'completed':
            console.log('🔥 COMPLETION TRIGGERED - Replacing background modal with success modal!');
            console.log('🔥 Completion data:', data);
            progressValue = 100;

            // IMMEDIATELY clear ALL progress-related state
            setRealTimeProgress(null);
            setUploading(false);
            setProgress(0);
            setBulkZipFile(null);
            if (bulkZipInputRef.current) bulkZipInputRef.current.value = '';

            // Data will be refreshed when success modal closes

            // FORCE the success modal to show by setting state synchronously
            const successResults = {
              success: true,
              type: 'bulk_zip_realtime',
              batchId: batchId,
              isCustomList: jobData?.type === 'custom_list',
              targetJob: jobData?.title || 'Unknown Job',
              stats: data.processed_count ? {
                processed: data.processed_count,
                failed: data.failed_count || 0,
                filter_stats: data.filter_stats || { passed: 0, failed: 0 }
              } : null,
              completionMessage: data.message
            };

            console.log('🔥 Setting success results:', successResults);
            setUploadResults(successResults);
            setShowResults(true);
            console.log('🔥 SUCCESS MODAL STATE UPDATED - showResults should be true');

            // Force a re-render to ensure modal shows immediately
            setTimeout(() => {
              console.log('🔥 Timeout check - showResults:', showResults, 'uploadResults:', uploadResults);
            }, 100);
            break;
          case 'failed':
            progressValue = 0;
            // Clear uploading state and reset form on failure too
            setUploading(false);
            setProgress(0);
            setBulkZipFile(null);
            if (bulkZipInputRef.current) bulkZipInputRef.current.value = '';

            setRealTimeProgress(null);
            setUploadResults({
              success: false,
              error: data.error || 'Unknown error occurred'
            });
            setShowResults(true);
            console.log('❌ BULK UPLOAD FAILED - Showing error modal');
            break;
        }

        if (data.status !== 'completed' && data.status !== 'failed') {
          setRealTimeProgress({
            progress: progressValue,
            status: data.status,
            message: progressMessage
          });
        }
      }
    });

    return unsubscribe;
  };

  // Function to download ZIP template with CSV and sample structure
  const downloadBulkTemplate = () => {
    // Create CSV content for the mapping file
    const csvContent = "first_name,last_name,email,pdf_filename\nJohn,Doe,john.doe@example.com,john-doe-cv.pdf\nJane,Smith,jane.smith@example.com,jane-smith-cv.pdf\nMike,Johnson,mike.johnson@example.com,mike-johnson-cv.pdf";

    // Create instructions text
    const instructionsContent = `BULK CV UPLOAD INSTRUCTIONS
============================

Your ZIP file should contain:

1. CVs in PDF format named as: firstname-lastname-cv.pdf
   Examples:
   - john-doe-cv.pdf
   - jane-smith-cv.pdf
   - mike-johnson-cv.pdf

2. A mapping file named "mapping.csv" or "mapping.xlsx" with these columns:
   - first_name: Candidate's first name
   - last_name: Candidate's last name  
   - email: Candidate's email address
   - pdf_filename: Exact name of the PDF file (must match the actual filename)

All fields are required. The PDF filename in the CSV must exactly match the actual PDF filename in the ZIP.

Example ZIP structure:
your-upload.zip
├── john-doe-cv.pdf
├── jane-smith-cv.pdf
├── mike-johnson-cv.pdf
└── mapping.csv

The system will:
1. Extract and validate all files
2. Apply AI filtering to each CV (if job description available)
3. Automatically sort candidates into appropriate stages
4. Process everything in the background - you can leave the page after upload

Need help? Contact support.`;

    // For now, just download the CSV template and instructions
    // In a real implementation, you'd create an actual ZIP file
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvLink = document.createElement('a');
    const csvUrl = URL.createObjectURL(csvBlob);
    csvLink.setAttribute('href', csvUrl);
    csvLink.setAttribute('download', 'mapping_template.csv');
    csvLink.style.visibility = 'hidden';
    document.body.appendChild(csvLink);
    csvLink.click();
    document.body.removeChild(csvLink);

    // Download instructions
    const instructionsBlob = new Blob([instructionsContent], { type: 'text/plain;charset=utf-8;' });
    const instructionsLink = document.createElement('a');
    const instructionsUrl = URL.createObjectURL(instructionsBlob);
    instructionsLink.setAttribute('href', instructionsUrl);
    instructionsLink.setAttribute('download', 'BULK_UPLOAD_INSTRUCTIONS.txt');
    instructionsLink.style.visibility = 'hidden';
    document.body.appendChild(instructionsLink);
    instructionsLink.click();
    document.body.removeChild(instructionsLink);
  };

  // Load job options when component opens
  // Load all job options (posted jobs + custom lists)
  const loadJobOptions = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      const options = await loadAllJobOptions(targetUid, { forceRefresh });
      setJobOptions(options);
    } catch (error) {
      console.error('Error loading job options:', error);
      setJobOptions([]);
    }
  }, [targetUid]);

  React.useEffect(() => {
    if (isOpen && targetUid) {
      loadJobOptions();
    }
  }, [isOpen, targetUid, loadJobOptions]);

  // Handle job selection
  const handleJobSelect = (jobId) => {
    if (jobId === 'create_new_list') {
      setShowCreateCustomList(true);
      setSelectedJob('');
      setSelectedJobData(null);
      return;
    }

    setSelectedJob(jobId);
    const job = jobOptions.find(j => j.id === jobId);
    setSelectedJobData(job);
    setShowCreateCustomList(false);
  };

  // Handle creating a new custom list
  const handleCreateCustomList = async () => {
    if (!customListTitle.trim()) {
      alert(uiText('Please enter a title for the custom list'));
      return;
    }

    try {
      const targetHireDate = normalizeDateInputValue(customListTargetHireDate);
      const listData = {
        title: customListTitle.trim(),
        description: customListDescription.trim(),
        jobDescription: customListJobDescription.trim(),
        targetHireDate,
        hiringPlan: {
          targetHireDate
        }
      };

      const newCustomList = await createCustomList(targetUid, listData);

      // Add to job options and select it
      const newOption = {
        id: newCustomList.listId,
        title: newCustomList.title,
        type: 'custom_list',
        subtitle: t('workspace.customListWithCount', { count: 0 }),
        applicantCount: 0,
        description: newCustomList.description,
        jobDescription: newCustomList.jobDescription,
        targetHireDate: newCustomList.targetHireDate,
        hiringPlan: newCustomList.hiringPlan,
        data: newCustomList
      };

      // FIRST: Update local job options immediately
      setJobOptions(prev => [...prev, newOption]);
      setSelectedJob(newCustomList.listId);
      setSelectedJobData(newOption);

      // SECOND: Reset custom list form
      setCustomListTitle('');
      setCustomListDescription('');
      setCustomListJobDescription('');
      setCustomListTargetHireDate('');
      setShowCreateCustomList(false);

      // THIRD: Force reload job options from database to sync with parent
      console.log('🔄 Reloading job options after custom list creation');
      await loadJobOptions({ forceRefresh: true });

      // FOURTH: Refresh parent data 
      if (onDataRefresh) {
        console.log('🔄 Refreshing parent data after custom list creation');
        await onDataRefresh();
      } else {
        console.log('⚠️ No onDataRefresh callback provided for custom list creation');
      }

      // Don't auto-select the new custom list - let user choose manually

      console.log('✅ Custom list created successfully:', newCustomList.title);

    } catch (error) {
      console.error('Error creating custom list:', error);
      alert(uiText('Error creating custom list. Please try again.'));
    }
  };

  // Handle single CV file selection
  const handleSingleCVSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSingleCV(file);
    } else {
      alert(uiText('Please select a PDF file'));
    }
  };

  // Handle bulk ZIP file selection
  const handleBulkZipSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'application/zip' || file.name.endsWith('.zip'))) {
      setBulkZipFile(file);
    } else {
      alert(uiText('Please select a ZIP file'));
      setBulkZipFile(null);
    }
  };

  // Upload file to Firebase Storage
  const uploadFileToStorage = async (file, path) => {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };

  // Filter CV using AI endpoint (routed through backend to avoid CORS)
  const filterCV = async (cvUrl, jobDescription) => {
    try {
      const response = await fetch(`${BACKEND_URL}/filter-single-cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resumePath: cvUrl,
          jobDescription: jobDescription
        }),
      });

      if (!response.ok) {
        console.error(`🔍 Filtering API error: ${response.status}`);
        // Fail open means the CV is still imported; it does not mean the
        // candidate was scored, promoted, or eligible for auto-advancement.
        return {
          passed: null,
          fit_score: null,
          evaluationStatus: 'technical_failure',
          initialEvaluation: null
        };
      }

      const data = await response.json();
      const evaluationStatus = data.status || (data.fit_score !== null && data.fit_score !== undefined
        ? 'scored'
        : 'technical_failure');
      const rawFitScore = data.fit_score;
      const fitScore = rawFitScore !== null && rawFitScore !== undefined && rawFitScore !== '' && Number.isInteger(Number(rawFitScore))
        && Number(rawFitScore) >= 0 && Number(rawFitScore) <= 100
        ? Number(rawFitScore)
        : null;
      const isScored = data.success !== false && evaluationStatus === 'scored' && fitScore !== null;
      const result = isScored ? data.analysis?.toLowerCase().trim() === 'proceed' : null;
      console.log(`🔍 AI Decision: ${data.analysis || evaluationStatus} → ${result === true ? 'PROCEED' : result === false ? 'REJECT' : 'REVIEW'} (${fitScore ?? 'no score'})`);

      return {
        passed: result,
        fit_score: isScored ? fitScore : null,
        evaluationStatus,
        initialEvaluation: isScored ? {
          status: 'scored',
          analysis: data.analysis,
          fit_score: fitScore,
          summary: data.summary || '',
          strengths: data.strengths || [],
          improvementAreas: data.improvementAreas || data.concerns || [],
          rationale: data.rationale || '',
          factors: data.factors || [],
          evaluatedAt: data.evaluatedAt || Date.now(),
          // The backend returns the canonical pre-connection ledger so the
          // eventual connection creation need not reconstruct score history.
          candidateEvaluation: data.candidateEvaluation || null
        } : null
      };
    } catch (error) {
      console.error('🔍 Error filtering CV:', error);
      console.log('🔍 IMPORTING FOR REVIEW due to filtering error');
      return {
        passed: null,
        fit_score: null,
        evaluationStatus: 'technical_failure',
        initialEvaluation: null
      };
    }
  };

  const triggerAutoScreeningAssessments = async (createdCandidates, targetJob) => {
    const passedCandidates = (createdCandidates || []).filter(candidate => candidate.passedFilter === true);
    if (!passedCandidates.length || !currentUser || !targetUid) return [];

    try {
      const token = await currentUser.getIdToken();
      const results = await Promise.allSettled(passedCandidates.map(async (candidate) => {
        const response = await fetch(`${BACKEND_URL}/auto-screening/advance`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            company_id: targetUid,
            applicant_id: candidate.tempId,
            job_id: targetJob?.id,
            connection_key: candidate.connectionKey,
            passed_filter: true
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.detail || data?.error || 'Auto-screening assessment delivery failed');
        }
        return data;
      }));

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.warn(`Auto-screening assessment could not be delivered for ${passedCandidates[index]?.name || 'candidate'}:`, result.reason);
        }
      });
      return results;
    } catch (error) {
      // The CV import itself is complete. The idempotent automation endpoint can
      // be retried without sending a duplicate invitation.
      console.warn('Auto-screening assessment delivery is pending:', error);
      return [];
    }
  };

  // Handle single CV upload
  const handleSingleUpload = async () => {
    if (!singleCV || !singleEmail || !singleName) {
      alert(uiText('Please fill in all fields'));
      return;
    }

    // No-job mode: create imported profile only (no AI filtering)
    if (allowNoJob && noJobMode) {
      try {
        setUploading(true);
        setProgress(0);
        // Use targetUid for storage path
        const cvPath = `cvs/${targetUid}/${Date.now()}_${singleCV.name}`;
        setProgress(25);
        const cvUrl = await uploadFileToStorage(singleCV, cvPath);
        setProgress(60);
        // Use targetUid for database write
        await createImportedProfile(targetUid, {
          email: singleEmail,
          name: singleName,
          cvLink: cvUrl,
          fileName: singleCV.name,
        });
        setProgress(100);
        setUploadResults({ success: true, type: 'single_unassigned', count: 1, results: [{ name: singleName, email: singleEmail }] });
        setShowResults(true);
        // reset form
        setSingleCV(null);
        setSingleEmail('');
        setSingleName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (error) {
        console.error('Error uploading CV (no job):', error);
        alert(uiText('Error uploading CV. Please try again.'));
        setUploadResults({ success: false, error: error.message });
        setShowResults(true);
      } finally {
        setUploading(false);
        setProgress(0);
      }
      return;
    }

    if (!selectedJobData) {
      alert(uiText('Please select a job/list'));
      return;
    }

    try {
      setUploading(true);
      setProgress(0);

      // Upload CV to storage (use targetUid)
      const cvPath = `cvs/${targetUid}/${Date.now()}_${singleCV.name}`;
      setProgress(25);

      const cvUrl = await uploadFileToStorage(singleCV, cvPath);
      setProgress(50);

      // Filter CV if job description is available (for both posted jobs and custom lists)
      let passedFilter = null;
      let fitScore = null;
      let initialEvaluation = null;
      let evaluationStatus = 'not_requested';
      const jobDescription = selectedJobData.jobDescription || selectedJobData.description || '';

      if (jobDescription && jobDescription.trim()) {
        setProgress(60);
        console.log(`🔍 Filtering CV: ${singleCV.name} for ${selectedJobData.title}`);

        const filterResult = await filterCV(cvUrl, jobDescription);
        passedFilter = filterResult.passed;
        fitScore = filterResult.fit_score;
        initialEvaluation = filterResult.initialEvaluation;
        evaluationStatus = filterResult.evaluationStatus;
        console.log(`🔍 Filter result: ${passedFilter === true ? 'PASSED' : passedFilter === false ? 'FAILED' : 'Review required'}`);
      } else {
        evaluationStatus = 'not_configured';
        console.log('🔍 No job description - importing into Initial Analysis without a score');
      }

      // Create imported profile and connection
      const cvData = {
        email: singleEmail,
        name: singleName,
        cvLink: cvUrl,
        fileName: singleCV.name,
        passedFilter, // Pass filter result to bulk upload function
        fit_score: fitScore,
        initialEvaluation,
        evaluationStatus
      };

      console.log(`📄 Creating candidate: ${cvData.name} (${cvData.email})`);

      setProgress(75);

      // Use targetUid
      const result = await bulkUploadCVsOptimized(targetUid, [cvData], selectedJobData);
      await triggerAutoScreeningAssessments(result.results, selectedJobData);
      try {
        await triggerCommunityAutoSync(currentUser);
      } catch (syncError) {
        console.warn('Community auto-sync failed after single CV upload:', syncError);
      }

      setProgress(100);
      setUploadResults({
        success: true,
        type: 'single',
        count: 1,
        results: result.results,
        isCustomList: result.isCustomList,
        filterResults: {
          passed: passedFilter === true ? 1 : 0,
          failed: passedFilter === false ? 1 : 0,
          technical_failure: evaluationStatus === 'technical_failure' ? 1 : 0,
          not_configured: evaluationStatus === 'not_configured' ? 1 : 0
        }
      });
      setShowResults(true);

      // Reset form
      setSingleCV(null);
      setSingleEmail('');
      setSingleName('');
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (error) {
      console.error('Error uploading single CV:', error);
      alert(uiText('Error uploading CV. Please try again.'));
      setUploadResults({
        success: false,
        error: error.message
      });
      setShowResults(true);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Handle bulk CV upload with ZIP file - Frontend implementation
  const handleBulkUpload = async () => {
    // No-job mode: support multiple PDF files locally (no AI filtering)
    if (allowNoJob && noJobMode) {
      if (!bulkLocalFiles || bulkLocalFiles.length === 0) {
        alert(uiText('Please select one or more PDF files'));
        return;
      }
      try {
        setUploading(true);
        setProgress(0);
        const results = [];
        for (let i = 0; i < bulkLocalFiles.length; i++) {
          const file = bulkLocalFiles[i];
          // Use targetUid for storage path
          const cvPath = `cvs/${targetUid}/${Date.now()}_${file.name}`;
          const cvUrl = await uploadFileToStorage(file, cvPath);
          const nameGuess = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
          // Use targetUid for database write
          await createImportedProfile(targetUid, {
            email: '',
            name: nameGuess,
            cvLink: cvUrl,
            fileName: file.name,
          });
          results.push({ name: nameGuess, email: '' });
          setProgress(Math.round(((i + 1) / bulkLocalFiles.length) * 100));
        }
        setUploadResults({ success: true, type: 'bulk_unassigned', count: results.length, results });
        setShowResults(true);
        setBulkLocalFiles([]);
      } catch (error) {
        console.error('Error bulk uploading CVs (no job):', error);
        alert(uiText('Error uploading files. Please try again.'));
        setUploadResults({ success: false, error: error.message });
        setShowResults(true);
      } finally {
        setUploading(false);
        setProgress(0);
      }
      return;
    }

    if (!bulkZipFile || !selectedJobData) {
      alert(uiText('Please select a ZIP file and target job/list'));
      return;
    }

    try {
      setUploading(true);
      setProgress(5);

      const batchId = `bulk_${Date.now()}`;

      // Load ZIP file
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(bulkZipFile);

      // 1. Look for any CSV file in the ZIP (for candidate mapping data)
      let mappingData = {}; // filename -> { email, name }
      const csvFile = Object.values(zipContent.files).find(
        f => !f.dir && f.name.toLowerCase().endsWith('.csv') && !f.name.startsWith('__MACOSX')
      );

      if (csvFile) {
        console.log('📄 Found CSV file:', csvFile.name);
        const csvText = await csvFile.async('string');
        const parseResult = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true
        });

        if (parseResult.data) {
          console.log('📄 Raw CSV Data:', parseResult.data);
          console.log('📄 CSV Headers:', parseResult.meta?.fields);

          parseResult.data.forEach(row => {
            // Get the filename - try multiple possible column names (original keys with underscores)
            const filename = row['pdf_filename'] || row['PDF_FILENAME'] || row['Pdf_Filename'] ||
              row['filename'] || row['Filename'] || row['FILENAME'] ||
              row['file'] || row['File'] || row['FILE'] ||
              row['pdffilename'] || row['PdfFilename'];

            if (filename && filename.trim()) {
              // Get first name - try multiple possible column names
              const firstName = row['first_name'] || row['First_Name'] || row['FIRST_NAME'] ||
                row['firstname'] || row['FirstName'] || row['Firstname'] ||
                row['first'] || row['First'] || row['FIRST'] || '';

              // Get last name - try multiple possible column names  
              const lastName = row['last_name'] || row['Last_Name'] || row['LAST_NAME'] ||
                row['lastname'] || row['LastName'] || row['Lastname'] ||
                row['last'] || row['Last'] || row['LAST'] || '';

              // Get email - try multiple possible column names
              const email = row['email'] || row['Email'] || row['EMAIL'] ||
                row['email_address'] || row['Email_Address'] || row['EMAIL_ADDRESS'] ||
                row['emailaddress'] || row['EmailAddress'] || '';

              const fullName = ((firstName || '') + ' ' + (lastName || '')).trim();

              // Store mapping keyed by cleaned filename
              const cleanFilename = filename.trim();
              mappingData[cleanFilename] = {
                name: fullName,
                email: (email || '').trim()
              };

              console.log(`📄 Mapping entry: "${cleanFilename}" -> Name: "${fullName}", Email: "${email}"`);
            }
          });
        }
        console.log('📄 Parsed mapping file:', Object.keys(mappingData).length, 'entries');
        console.log('📄 Mapping keys:', Object.keys(mappingData));
      }

      // 2. Identify PDF files
      const pdfEntries = [];
      zipContent.forEach((relativePath, zipEntry) => {
        if (!zipEntry.dir && zipEntry.name.toLowerCase().endsWith('.pdf') && !zipEntry.name.startsWith('__MACOSX')) {
          pdfEntries.push(zipEntry);
        }
      });

      if (pdfEntries.length === 0) {
        throw new Error('No PDF files found in the ZIP archive.');
      }

      setProgress(10);

      const processedCVs = [];
      const jobDescription = selectedJobData.jobDescription || selectedJobData.description || '';
      const totalFiles = pdfEntries.length;

      // 3. Process each PDF
      for (let i = 0; i < totalFiles; i++) {
        const zipEntry = pdfEntries[i];

        // Extract blob
        const blob = await zipEntry.async('blob');
        const file = new File([blob], zipEntry.name.split('/').pop(), { type: 'application/pdf' });

        // Upload to Storage
        const cvPath = `cvs/${targetUid}/${Date.now()}_${file.name}`;
        const cvUrl = await uploadFileToStorage(file, cvPath);

        // Get metadata from mapping or guess
        const fullPath = zipEntry.name;
        const filenameOnly = fullPath.split('/').pop();

        // Try exact match, then filename match, then fuzzy match
        let mapping = mappingData[fullPath] || mappingData[filenameOnly];

        // If not found, try finding a mapping key that ends with this filename
        if (!mapping) {
          const foundKey = Object.keys(mappingData).find(k => fullPath.endsWith(k) || k.endsWith(filenameOnly));
          if (foundKey) mapping = mappingData[foundKey];
        }

        const candidateName = (mapping?.name && mapping.name.trim() !== '') ? mapping.name : filenameOnly.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        const candidateEmail = mapping?.email || '';

        console.log(`📄 Processing: ${filenameOnly} -> Name: ${candidateName}, Email: ${candidateEmail}`);

        // Filter CV if Job Description exists
        let passedFilter = null;
        let fitScore = null;
        let initialEvaluation = null;
        let evaluationStatus = 'not_requested';
        if (jobDescription && jobDescription.trim()) {
          const filterResult = await filterCV(cvUrl, jobDescription);
          passedFilter = filterResult.passed;
          fitScore = filterResult.fit_score;
          initialEvaluation = filterResult.initialEvaluation;
          evaluationStatus = filterResult.evaluationStatus;
        } else {
          evaluationStatus = 'not_configured';
        }

        processedCVs.push({
          email: candidateEmail,
          name: candidateName,
          cvLink: cvUrl,
          fileName: filenameOnly,
          passedFilter,
          fit_score: fitScore,
          initialEvaluation,
          evaluationStatus
        });

        // Update progress (10% to 90%)
        const percentage = 10 + Math.round(((i + 1) / totalFiles) * 80);
        setProgress(percentage);
      }

      // 4. Save to RTDB using Optimized function
      setProgress(90);
      const result = await bulkUploadCVsOptimized(targetUid, processedCVs, selectedJobData);
      console.log('✅ Bulk upload saved to RTDB:', result);
      await triggerAutoScreeningAssessments(result.results, selectedJobData);
      try {
        await triggerCommunityAutoSync(currentUser);
      } catch (syncError) {
        console.warn('Community auto-sync failed after bulk CV upload:', syncError);
      }

      setProgress(100);

      // 5. Show Success Results
      setUploadResults({
        success: true,
        type: 'bulk_zip',
        batchId: batchId,
        isCustomList: result.isCustomList,
        targetJob: selectedJobData.title || selectedJobData.jobOpening,
        stats: {
          processed: processedCVs.length,
          filter_stats: {
            passed: processedCVs.filter(c => c.passedFilter === true).length,
            failed: processedCVs.filter(c => c.passedFilter === false).length,
            technical_failure: processedCVs.filter(c => c.evaluationStatus === 'technical_failure').length,
            not_configured: processedCVs.filter(c => c.evaluationStatus === 'not_configured').length
          }
        },
        zipFileName: bulkZipFile.name
      });
      setShowResults(true);

      // Clean up
      setBulkZipFile(null);
      if (bulkZipInputRef.current) bulkZipInputRef.current.value = '';

    } catch (error) {
      console.error('Error uploading bulk ZIP:', error);
      alert('Error uploading ZIP file: ' + error.message);

      setUploading(false);
      setProgress(0);
      setBulkZipFile(null);
      if (bulkZipInputRef.current) bulkZipInputRef.current.value = '';

      setUploadResults({
        success: false,
        error: error.message
      });
      setShowResults(true);
    } finally {
      setUploading(false); // Ensure uploading is false at end
    }
  };

  // Handle results close
  const handleResultsClose = () => {
    setShowResults(false);
    setUploadResults(null);
    setBatchStatus(null);
    setRealTimeProgress(null);

    // Clean up any Firebase listeners
    if (uploadResults?.batchId) {
      // Use targetUid for batch status listener
      const batchRef = dbRef(database, `bulk_uploads/${targetUid}/${uploadResults.batchId}`);
      off(batchRef);
    }

    // Always trigger onUploadComplete to close modal and reload data
    if (onUploadComplete) {
      console.log('🔄 Triggering page reload via onUploadComplete callback');
      onUploadComplete();
    } else {
      console.log('⚠️ No onUploadComplete callback provided');
    }
  };

  if (!isOpen) return null;

  if (showResults) {
    return (
      <div className="cv-upload-overlay" onClick={(e) => e.target.classList.contains('cv-upload-overlay') && handleResultsClose()}>
        <div className="cv-upload-modal results-modal">
          <div className="modal-header">
            <h2>{uiText("Upload Results")}</h2>
            <div className="close" onClick={handleResultsClose}>&times;</div>
          </div>

          <div className="results-content">
            {uploadResults?.success ? (
              <>
                <div className="success-icon">
                  <i className="fa-solid fa-check-circle"></i>
                </div>
                <h3>{t('cvUpload.uploadSuccessful')}</h3>

                {uploadResults.type === 'single_unassigned' ? (
                  <>
                    <p>{t('cvUpload.storedSingleUnassigned')}</p>
                  </>
                ) : uploadResults.type === 'bulk_unassigned' ? (
                  <>
                    <p>{t('cvUpload.storedBulkUnassigned', { count: uploadResults.count })}</p>
                    <div className="uploaded-files">
                      <h4>{t('cvUpload.uploadedFiles')}:</h4>
                      <ul>
                        {uploadResults.results?.map((r, idx) => (
                          <li key={idx}><strong>{r.name}</strong></li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : uploadResults.type === 'bulk_zip_immediate' ? (
                  <>
                    <p>{t('cvUpload.zipUploadedTo', { job: uploadResults.targetJob })}</p>

                    {uploadResults.isCustomList && (
                      <p className="custom-list-info">{t('cvUpload.addedToCustomList', { job: uploadResults.targetJob })}</p>
                    )}

                    <div className="uploaded-files">
                      <h4>{uiText("Processing Details:")}</h4>
                      <p><strong>{uiText("ZIP File:")}</strong> {uploadResults.zipFileName}</p>
                      <p><strong>{uiText("Status:")}</strong>{uiText(" Upload completed successfully")}</p>
                      <p><strong>{uiText("Processing:")}</strong>{uiText(" CVs are being processed in the background")}</p>
                    </div>

                    <div style={{
                      backgroundColor: '#e8f4fd',
                      padding: '15px',
                      borderRadius: '6px',
                      marginTop: '15px',
                      border: '1px solid #b8daff',
                      textAlign: 'center'
                    }}>
                      <p style={{ margin: 0, color: '#004085', fontSize: '14px' }}>
                        <i className="fa-solid fa-info-circle" style={{ marginRight: '5px' }}></i>{uiText("Processing will continue in the background. New applicants will appear shortly.")}</p>
                    </div>
                  </>
                ) : (uploadResults.type === 'bulk_zip' || uploadResults.type === 'bulk_zip_realtime') ? (
                  <>
                    <p>{uiText("Successfully uploaded ")}{uploadResults.stats?.processed || uiText("multiple")}{uiText(" CV")}{(uploadResults.stats?.processed !== 1) ? 's' : ''}{uiText(" to ")}{uploadResults.targetJob}</p>

                    {uploadResults.isCustomList && (
                      <p className="custom-list-info">{uiText("Added to custom list: ")}{uploadResults.targetJob}</p>
                    )}

                    {uploadResults.stats?.filter_stats && (
                      <div className="filter-results-info">
                        <h4>{t('cvUpload.aiFilteringResults')}:</h4>
                        <p>
                          <span style={{ color: 'var(--app-tone-28a745, #28a745)', fontWeight: "var(--app-weight-strong, bold)" }}>
                            ✅ {t('cvUpload.candidatesPassed', { count: uploadResults.stats.filter_stats.passed })}
                          </span>
                          {uploadResults.stats.filter_stats.passed > 0 && !uploadResults.isCustomList && ` (${t('cvUpload.movedToFit')})`}
                          {uploadResults.stats.filter_stats.passed > 0 && uploadResults.isCustomList && ` (${t('cvUpload.keptInList')})`}
                        </p>
                        {uploadResults.stats.filter_stats.failed > 0 && (
                          <p>
                            <span style={{ color: 'var(--app-tone-dc3545, #dc3545)', fontWeight: "var(--app-weight-strong, bold)" }}>
                              ❌ {uploadResults.stats.filter_stats.failed}{uiText(" candidates filtered out")}</span>
                            {uiText(" (placed in All Applicants)")}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : uploadResults.type === 'bulk_background' ? (
                  <>
                    <p>{uiText("Successfully uploaded ZIP file to ")}{uploadResults.targetJob}</p>
                    <div className="background-processing-info">
                      <div className="processing-icon">
                        <i className="fa-solid fa-cogs fa-spin"></i>
                      </div>
                      <h4>{t('cvUpload.backgroundProcessingStarted')}</h4>
                      <p>{t('cvUpload.processingInBackground')}</p>
                      <p><strong>{t('cvUpload.safeToClose')}</strong> {t('cvUpload.processingContinue')}</p>
                      <p className="processing-note">
                        <i className="fa-solid fa-clock"></i>{uiText("Processing typically takes 30-60 seconds per CV.")}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <p>{uiText("Successfully uploaded ")}{uploadResults.count}{uiText(" CV")}{uploadResults.count > 1 ? 's' : ''}{uiText(" to ")}{selectedJobData?.title}</p>

                    {uploadResults.type === 'bulk' && (
                      <p className="batch-info">{uiText("Batch ID: ")}{uploadResults.batchId}</p>
                    )}

                    {uploadResults.isCustomList && (
                      <p className="custom-list-info">{uiText("Added to custom list: ")}{selectedJobData?.title}</p>
                    )}

                    {uploadResults.filterResults && (
                      <div className="filter-results-info">
                        <h4>{t('cvUpload.aiFilteringResults')}:</h4>
                        <p>
                          <span style={{ color: 'var(--app-tone-28a745, #28a745)', fontWeight: "var(--app-weight-strong, bold)" }}>
                            ✅ {t('cvUpload.candidatesPassed', { count: uploadResults.filterResults.passed })}
                          </span>
                          {uploadResults.filterResults.passed > 0 && !uploadResults.isCustomList && ` (${t('cvUpload.movedToFit')})`}
                          {uploadResults.filterResults.passed > 0 && uploadResults.isCustomList && ` (${t('cvUpload.keptInList')})`}
                        </p>
                        {uploadResults.filterResults.failed > 0 && (
                          <p>
                            <span style={{ color: 'var(--app-tone-dc3545, #dc3545)', fontWeight: "var(--app-weight-strong, bold)" }}>
                              ❌ {t('cvUpload.candidatesFiltered', { count: uploadResults.filterResults.failed })}
                            </span>
                            {` (${t('cvUpload.placedInAll')})`}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="uploaded-files">
                      <h4>{uiText("Uploaded Files:")}</h4>
                      <ul>
                        {uploadResults.results?.map((result, index) => (
                          <li key={index}>
                            <strong>{result.name}</strong> - {result.email}
                            {result.substageId && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '12px',
                                color: result.passedFilter ? 'var(--app-tone-28a745, #28a745)' : '#666',
                                fontStyle: 'italic'
                              }}>
                                ({result.passedFilter ? uiText("✅ Passed Filter") : uiText("📋 All Applicants")})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="error-icon">
                  <i className="fa-solid fa-exclamation-circle"></i>
                </div>
                <h3>{uiText("Upload Failed")}</h3>
                <p>{uiText("Error: ")}{uploadResults?.error}</p>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn-primary" onClick={handleResultsClose}>
              {t('cvUpload.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-upload-overlay" onClick={(e) => e.target.classList.contains('cv-upload-overlay') && onClose()}>
      <div className="cv-upload-modal" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div className="modal-header">
          <h2>{t('cvUpload.title')}</h2>
          <div className="close" onClick={onClose} style={{ [isRTL ? 'left' : 'right']: '20px', [isRTL ? 'right' : 'left']: 'auto' }}>&times;</div>
        </div>

        <div className="upload-content">
          {/* Upload Mode Selection */}
          {/* Upload Mode Selection */}
          <div className="upload-mode-section">
            <h3>{t('cvUpload.uploadMode')}</h3>
            <div className="mode-buttons">
              <button
                className={`mode-btn ${uploadMode === 'single' ? 'active' : ''}`}
                onClick={() => setUploadMode('single')}
              >
                <i className="fa-solid fa-file"></i>
                {t('cvUpload.singleCV')}
              </button>
              <button
                className={`mode-btn ${uploadMode === 'bulk' ? 'active' : ''}`}
                onClick={() => canBulkUpload ? setUploadMode('bulk') : setShowUpgradeModal(true)}
              >
                <i className="fa-solid fa-layer-group"></i>
                {t('cvUpload.bulkUpload')}
              </button>
            </div>
          </div>

          {/* Job Selection */}
          {/* Job Selection */}
          <div className="job-selection-section">
            <h3>{t('cvUpload.selectTargetJob')}</h3>
            {allowNoJob && (
              <div className="no-job-toggle">
                <input
                  id="no-job-toggle"
                  type="checkbox"
                  checked={noJobMode}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setNoJobMode(v);
                    if (v) { setSelectedJob(''); setSelectedJobData(null); }
                  }}
                />
                <label htmlFor="no-job-toggle">{t('cvUpload.noJobYet')}</label>
              </div>
            )}
            <select
              value={selectedJob}
              onChange={(e) => handleJobSelect(e.target.value)}
              className="job-select"
              disabled={allowNoJob && noJobMode}
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <option value="">{t('cvUpload.chooseJobOrList')}</option>
              {jobOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {uiText(option.title)} ({formatJobSubtitle(option)})
                </option>
              ))}
              <option value="create_new_list">{t('cvUpload.createNewCustomList')}</option>
            </select>
            {allowNoJob && noJobMode && (
              <p style={{ color: '#666', fontSize: '12px', marginTop: '6px' }}>
                {t('cvUpload.noJobDesc')}
              </p>
            )}
          </div>

          {/* Create Custom List Form */}
          {showCreateCustomList && (
            <div className="custom-list-creation">
              <h3>{t('cvUpload.createNewCustomListTitle')}</h3>

              <div className="form-group">
                <label>{t('cvUpload.listTitle')} *</label>
                <input
                  type="text"
                  value={customListTitle}
                  onChange={(e) => setCustomListTitle(e.target.value)}
                  placeholder={t('cvUpload.listTitlePlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('cvUpload.description')} ({t('cvUpload.optional')})</label>
                <input
                  type="text"
                  value={customListDescription}
                  onChange={(e) => setCustomListDescription(e.target.value)}
                  placeholder={t('cvUpload.descriptionPlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('cvUpload.targetHireDate', 'Target hire date')} ({t('cvUpload.optional')})</label>
                <input
                  type="date"
                  value={customListTargetHireDate}
                  onChange={(e) => setCustomListTargetHireDate(e.target.value)}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('cvUpload.jobDescription')}</label>
                <textarea
                  value={customListJobDescription}
                  onChange={(e) => setCustomListJobDescription(e.target.value)}
                  placeholder={t('cvUpload.jobDescriptionPlaceholder')}
                  className="text-area"
                  rows="4"
                />
              </div>

              <div className="custom-list-buttons">
                <button
                  className="btn-secondary"
                  onClick={() => setShowCreateCustomList(false)}
                >
                  {t('cvUpload.cancel')}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateCustomList}
                  disabled={!customListTitle.trim()}
                >
                  {t('cvUpload.createList')}
                </button>
              </div>
            </div>
          )}

          {/* Single CV Upload */}
          {uploadMode === 'single' && (
            <div className="single-upload-section">
              <h3>{t('cvUpload.singleCVUpload')}</h3>

              <div className="form-group">
                <label>{t('cvUpload.cvFile')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleSingleCVSelect}
                  className="file-input"
                />
                {singleCV && <p className="file-selected">{t('cvUpload.selected')}: {singleCV.name}</p>}
              </div>

              <div className="form-group">
                <label>{t('cvUpload.candidateName')}</label>
                <input
                  type="text"
                  value={singleName}
                  onChange={(e) => setSingleName(e.target.value)}
                  placeholder={t('cvUpload.enterCandidateName')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('cvUpload.emailAddress')}</label>
                <input
                  type="email"
                  value={singleEmail}
                  onChange={(e) => setSingleEmail(e.target.value)}
                  placeholder={t('cvUpload.enterCandidateEmail')}
                  className="text-input"
                />
              </div>
            </div>
          )}

          {/* Bulk Upload - With Job (ZIP) */}
          {uploadMode === 'bulk' && !noJobMode && (
            <div className="bulk-upload-section">
              <h3 style={{ color: '#568ded', marginBottom: '20px', fontSize: '20px' }}>
                <i className="fa-solid fa-layer-group" style={{ marginInlineEnd: '10px' }}></i>
                {t('cvUpload.bulkCVUpload')}
              </h3>

              {/* Instructions */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                marginBottom: '25px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: "var(--app-neutral-ink, #333)", fontSize: '16px' }}>
                  <i className="fa-solid fa-star" style={{ marginInlineEnd: '8px', color: '#568ded' }}></i>
                  {t('cvUpload.professionalImportProcess')}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-file-pdf" style={{ fontSize: '16px', marginInlineEnd: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{t('cvUpload.nameCVsInstruction')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-table" style={{ fontSize: '16px', marginInlineEnd: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{t('cvUpload.createMappingInstruction')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-file-zipper" style={{ fontSize: '16px', marginInlineEnd: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{t('cvUpload.packageZipInstruction')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-robot" style={{ fontSize: '16px', marginInlineEnd: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{t('cvUpload.aiProcessInstruction')}</span>
                  </div>
                </div>
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#e8f4fd',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}>
                  <button
                    onClick={downloadBulkTemplate}
                    type="button"
                    style={{ '--light-control-fill': '#568ded',
                      padding: '8px 16px',
                      backgroundColor: '#568ded',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'Montserrat',
                      fontWeight: "var(--app-weight-strong, bold)",
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#4a7bd1';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#568ded';
                    }}
                    data-light-control={"filled"}>
                    <i className="fa-solid fa-download" style={{ marginInlineEnd: '5px' }}></i>
                    {t('cvUpload.downloadTemplate')}
                  </button>
                </div>
              </div>

              <div style={{
                backgroundColor: '#e8f4fd',
                padding: '15px',
                borderRadius: '6px',
                marginBottom: '25px',
                border: '1px solid #b8daff'
              }}>
                <p style={{ margin: 0, color: '#004085', fontSize: '14px' }}>
                  <i className="fa-solid fa-stopwatch" style={{ marginInlineEnd: '8px' }}></i>
                  <strong>{t('cvUpload.quickSetup')}:</strong> {t('cvUpload.quickSetupDesc')}
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: "var(--app-neutral-ink, #333)",
                  fontWeight: "var(--app-weight-strong, bold)",
                  fontSize: '14px'
                }}>
                  {t('cvUpload.zipFileLabel')}
                </label>
                <input
                  ref={bulkZipInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleBulkZipSelect}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: '#fff'
                  }}
                />
                {bulkZipFile && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#d4edda',
                    borderRadius: '6px',
                    border: '1px solid #c3e6cb',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <i className="fa-solid fa-file-zipper" style={{
                      fontSize: '18px',
                      color: 'var(--app-tone-155724, #155724)',
                      marginRight: '10px'
                    }}></i>
                    <span style={{ color: 'var(--app-tone-155724, #155724)', fontSize: '14px' }}>
                      <strong>{t('cvUpload.selected')}:</strong> {bulkZipFile.name} ({(bulkZipFile.size / 1024 / 1024).toFixed(2)}{uiText(" MB)")}</span>
                  </div>
                )}
              </div>

              {/* Processing Info */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: "var(--app-neutral-ink, #333)", fontSize: '16px' }}>
                  <i className="fa-solid fa-cogs" style={{ marginRight: '8px', color: '#568ded' }}></i>{uiText("What happens after upload:")}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-upload" style={{ fontSize: '16px', marginRight: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{uiText("ZIP uploaded to secure storage")}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-file-zipper" style={{ fontSize: '16px', marginRight: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{uiText("Files extracted and validated")}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-robot" style={{ fontSize: '16px', marginRight: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{uiText("AI filtering applied to each CV")}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fa-solid fa-users" style={{ fontSize: '16px', marginRight: '10px', color: '#568ded' }}></i>
                    <span style={{ fontSize: '13px', color: '#666' }}>{uiText("Candidates sorted into stages")}</span>
                  </div>
                </div>
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--app-tone-856404, #856404)' }}>
                    <i className="fa-solid fa-clock" style={{ marginRight: '5px' }}></i>
                    <strong>{uiText("Processing time:")}</strong>{uiText(" ~30-60 seconds per CV. You can safely close this window after upload begins.")}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Upload - No Job (Multiple PDFs) */}
          {uploadMode === 'bulk' && allowNoJob && noJobMode && (
            <div className="bulk-upload-section">
              <h3 style={{ color: '#568ded', marginBottom: '20px', fontSize: '20px' }}>
                <i className="fa-solid fa-layer-group" style={{ marginInlineEnd: '10px' }}></i>
                {t('cvUpload.bulkCVUploadUnassigned')}
              </h3>
              <div className="form-group">
                <label>{t('cvUpload.pdfFilesLabel')}</label>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={(e) => setBulkLocalFiles(Array.from(e.target.files || []))}
                  className="file-input"
                />
                {bulkLocalFiles.length > 0 && (
                  <p className="file-selected">{t('cvUpload.selectedFiles', { count: bulkLocalFiles.length })}</p>
                )}
              </div>
              <div style={{
                backgroundColor: '#fff3cd',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #ffeeba',
                marginTop: '8px'
              }}>
                <p style={{ margin: 0, color: 'var(--app-tone-856404, #856404)', fontSize: '13px' }}>
                  {t('cvUpload.aiFilteringDisabled')}
                </p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {(uploading || realTimeProgress) && (
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '25px',
              borderRadius: '10px',
              border: '1px solid #dee2e6',
              marginTop: '20px'
            }}>
              {/* Loading Header */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #568ded',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 10px auto'
                }}></div>
                <style>
                  {`
                     @keyframes spin {
                       0% { transform: rotate(0deg); }
                       100% { transform: rotate(360deg); }
                     }
                   `}
                </style>
                <h3 style={{ margin: 0, color: "var(--app-neutral-ink, #333)", fontSize: '18px' }}>
                  {t('cvUpload.processingUpload')}
                </h3>
              </div>

              {/* Upload Details */}
              <div style={{
                backgroundColor: 'white',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                {noJobMode ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.mode')}:</strong><br />
                      <span style={{ color: '#666' }}>{t('cvUpload.unassignedUpload')}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.selectedFiles')}:</strong><br />
                      <span style={{ color: '#666' }}>{bulkLocalFiles.length || (singleCV ? 1 : 0)}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.status')}:</strong><br />
                      <span style={{ color: '#666', fontFamily: 'monospace' }}>{uploading ? t('cvUpload.uploading') : (realTimeProgress?.status || '')}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.targetJob')}:</strong><br />
                      <span style={{ color: '#666' }}>{selectedJobData?.title}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.zipFile')}:</strong><br />
                      <span style={{ color: '#666' }}>{bulkZipFile?.name || (batchStatus?.zipFileName)}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.fileSize')}:</strong><br />
                      <span style={{ color: '#666' }}>{bulkZipFile ? (bulkZipFile.size / 1024 / 1024).toFixed(2) : '0'} MB</span>
                    </div>
                    <div>
                      <strong style={{ color: '#568ded' }}>{t('cvUpload.status')}:</strong><br />
                      <span style={{ color: '#666', fontFamily: 'monospace' }}>
                        {realTimeProgress ? realTimeProgress.status : t('cvUpload.uploading')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              <div style={{
                backgroundColor: '#e9ecef',
                borderRadius: '10px',
                height: '12px',
                overflow: 'hidden',
                marginBottom: '15px'
              }}>
                <div
                  style={{
                    width: `${realTimeProgress ? realTimeProgress.progress : progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #568ded 0%, #4a7bd1 100%)',
                    borderRadius: '10px',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>

              {/* Progress Steps */}
              {!noJobMode ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    <span style={{ color: progress >= 30 ? '#568ded' : '#999' }}>
                      <i className={`fa-solid ${progress >= 30 ? 'fa-check' : 'fa-circle'}`} style={{ marginInlineEnd: '5px' }}></i>
                      {t('cvUpload.zipUpload')}
                    </span>
                    <span style={{ color: progress >= 50 ? '#568ded' : '#999' }}>
                      <i className={`fa-solid ${progress >= 50 ? 'fa-check' : 'fa-circle'}`} style={{ marginInlineEnd: '5px' }}></i>
                      {t('cvUpload.extracting')}
                    </span>
                    <span style={{ color: progress >= 70 ? '#568ded' : '#999' }}>
                      <i className={`fa-solid ${progress >= 70 ? 'fa-check' : 'fa-circle'}`} style={{ marginInlineEnd: '5px' }}></i>
                      {t('cvUpload.validating')}
                    </span>
                    <span style={{ color: progress >= 90 ? '#568ded' : '#999' }}>
                      <i className={`fa-solid ${progress >= 90 ? 'fa-check' : 'fa-circle'}`} style={{ marginInlineEnd: '5px' }}></i>
                      {t('cvUpload.processing')}
                    </span>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '15px' }}>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      {realTimeProgress ? realTimeProgress.message : (
                        <>
                          {progress < 30 && t('cvUpload.uploadingZip')}
                          {progress >= 30 && progress < 50 && t('cvUpload.zipUploadedSuccessful')}
                          {progress >= 50 && progress < 70 && t('cvUpload.extractingCVs')}
                          {progress >= 70 && progress < 90 && t('cvUpload.validatingFiles')}
                          {progress >= 90 && t('cvUpload.applyingAIFilter')}
                        </>
                      )}
                    </p>
                  </div>
                  {progress >= 30 && (
                    <div style={{
                      backgroundColor: '#e8f4fd',
                      padding: '10px',
                      borderRadius: '6px',
                      marginTop: '15px',
                      border: '1px solid #b8daff',
                      textAlign: 'center'
                    }}>
                      <p style={{ margin: 0, color: '#004085', fontSize: '12px' }}>
                        <i className="fa-solid fa-info-circle" style={{ marginInlineEnd: '5px' }}></i>
                        <strong>{t('cvUpload.safeToClosePopup')}</strong> {t('cvUpload.processingInBackground')}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    {t('cvUpload.uploadingToUnassigned')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={uploading}>
            {t('cvUpload.cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={uploadMode === 'single' ? handleSingleUpload : handleBulkUpload}
            disabled={
              uploading ||
              (
                !noJobMode && !selectedJobData
              ) || (
                uploadMode === 'bulk' && (
                  (!noJobMode && !bulkZipFile) || (noJobMode && bulkLocalFiles.length === 0)
                )
              )
            }
          >
            {uploading ? t('cvUpload.uploading') : (
              uploadMode === 'single' ? t('cvUpload.uploadSingleCV') : (noJobMode ? t('cvUpload.uploadCVs') : t('cvUpload.uploadZipFile'))
            )}
          </button>
        </div>
      </div>

      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          requiredFeature="bulkUpload"
          featureDisplayName="Bulk CV Upload"
        />
      )}
    </div>
  );
};

export default CVUploadManager;
